
import React from 'react';
import { CardContent } from '@/components/ui/card';
import { Users, BarChart3, Music } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { SpotifyArtistProfile } from '@/types/spotify';
import TrackPreview from '../TrackPreview';
import { isValidSoundCloudUrl } from '@/utils/soundcloudUtils';

interface MusicTabContentProps {
  spotifyProfile: SpotifyArtistProfile | undefined;
}

const MusicTabContent: React.FC<MusicTabContentProps> = ({ spotifyProfile }) => {
  if (!spotifyProfile) {
    return (
      <CardContent className="space-y-4">
        <div className="text-center py-8">
          <Music className="h-12 w-12 mx-auto mb-3 text-white/60" />
          <p className="text-white/85 font-medium">No Spotify data available</p>
        </div>
      </CardContent>
    );
  }
  
  // Ensure we have valid topTracks data
  const tracks = Array.isArray(spotifyProfile.topTracks) ? spotifyProfile.topTracks : [];
  
  // Log SoundCloud URLs for debugging
  tracks.forEach(track => {
    if (track.soundcloudUrl) {
      console.log(`Track "${track.name}" SoundCloud URL: ${track.soundcloudUrl} (valid: ${isValidSoundCloudUrl(track.soundcloudUrl)})`);
    } else {
      console.log(`Track "${track.name}" has no SoundCloud URL`);
    }
  });
  
  return (
    <CardContent>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 backdrop-blur-sm bg-black/40 rounded-full px-3.5 py-1.5 border border-white/20">
          <Users className="h-4 w-4 text-neon-blue" />
          <span className="text-sm text-white/95">
            <span className="font-medium">{spotifyProfile.monthlyListeners?.toLocaleString()}</span> listeners
          </span>
        </div>
        <div className="flex items-center gap-2 backdrop-blur-sm bg-black/40 rounded-full px-3.5 py-1.5 border border-white/20">
          <BarChart3 className="h-4 w-4 text-neon-purple" />
          <span className="text-sm text-white/95">
            <span className="font-medium">{spotifyProfile.popularity}</span>/100 popularity
          </span>
        </div>
      </div>
      
      <Separator className="my-4 bg-white/20" />
      
      <div className="my-3">
        <h4 className="text-sm font-medium mb-3 text-white/95 flex items-center gap-2">
          <Music className="h-4 w-4 text-neon-pink" />
          Top Tracks
        </h4>
        <div className="space-y-3">
          {tracks.length > 0 ? (
            tracks.map((track) => (
              <TrackPreview key={track.id} track={track} />
            ))
          ) : (
            <div className="flex items-center justify-center bg-black/30 rounded-md p-4">
              <span className="text-xs text-white/70">No tracks available</span>
            </div>
          )}
        </div>
      </div>
      
      {spotifyProfile.audioFeatures && (
        <>
          <Separator className="my-4 bg-white/20" />
          
          <div className="mt-3">
            <h4 className="text-sm font-medium mb-3 text-white/95">Musical Style</h4>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-2.5 rounded-lg bg-black/40 border border-white/20">
                <p className="text-white/85 text-xs mb-1 font-medium">Tempo</p>
                <p className="font-medium text-white/95">{Math.round(spotifyProfile.audioFeatures.averageTempo)} BPM</p>
              </div>
              <div className="p-2.5 rounded-lg bg-black/40 border border-white/20">
                <p className="text-white/85 text-xs mb-1 font-medium">Energy</p>
                <div className="w-full bg-black/50 rounded-full h-2 mt-2 overflow-hidden border border-white/20">
                  <div 
                    className="bg-neon-orange h-full rounded-full relative overflow-hidden" 
                    style={{ width: `${spotifyProfile.audioFeatures.averageEnergy * 100}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                  </div>
                </div>
              </div>
              <div className="p-2.5 rounded-lg bg-black/40 border border-white/20">
                <p className="text-white/85 text-xs mb-1 font-medium">Mood</p>
                <p className="font-medium capitalize text-white/95">
                  {spotifyProfile.audioFeatures.moodProfile && 
                    Object.entries(spotifyProfile.audioFeatures.moodProfile)
                      .sort(([, a], [, b]) => b - a)[0][0]
                  }
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-black/40 border border-white/20">
                <p className="text-white/85 text-xs mb-1 font-medium">Danceability</p>
                <div className="w-full bg-black/50 rounded-full h-2 mt-2 overflow-hidden border border-white/20">
                  <div 
                    className="bg-neon-pink h-full rounded-full relative overflow-hidden" 
                    style={{ width: `${spotifyProfile.audioFeatures.averageDanceability * 100}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </CardContent>
  );
};

export default MusicTabContent;
